import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Input } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { Mail, MoveLeft } from 'lucide-react'
import { HeadProvider, Title } from 'react-head'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import LogoImg from '@/assets/images/logo.svg?url'
import { ROUTES } from '@/common/constants/routes'
import { useModalApplication } from '@/common/hooks/useModalApplication'
import { useRecoveryPassword } from '../../hooks/useRecoveryPassword'
import {
  ForgotPasswordForm,
  ForgotPasswordSchema,
} from '../../schemas/ForgotPasswordSchema'

export default function ForgotPassword() {
  const { t } = useTranslation(['login', 'common'])
  const { handleModal, resetModal } = useModalApplication()

  const hookForm = useForm<ForgotPasswordForm>({
    resolver: zodResolver(ForgotPasswordSchema),
    mode: 'onSubmit',
    defaultValues: { email: '' },
  })

  const {
    handleRecoveryPassword: recoveryPassword,
    isPendingRecoveryPassword: isPending,
  } = useRecoveryPassword()

  const handleRecoveryPassword = (data: ForgotPasswordForm) => {
    recoveryPassword(data.email)
  }

  return (
    <div className="flex flex-col gap-32 h-full w-full">
      <HeadProvider>
        <Title>{t('forgot_password.browser.title')}</Title>
      </HeadProvider>

      <img
        src={LogoImg}
        alt={t('sign_in.logo.alt')}
        className="w-1/4 mx-auto"
      />

      <FormProvider {...hookForm}>
        <form
          onSubmit={hookForm.handleSubmit(handleRecoveryPassword)}
          className="flex flex-col gap-6"
        >
          <div className="text-left mb-6">
            <h1 className="text-2xl font-semibold text-brand-900 mb-2">
              {t('forgot_password.title')}
            </h1>
            <p className="text-base-600 text-sm">
              {t('forgot_password.subtitle')}
            </p>
          </div>

          <Input.Icon
            size="lg"
            name="email"
            placeholder={t('forgot_password.email.placeholder')}
            icon={<Mail className="text-base-500 size-4" />}
            iconPosition="left"
            type="email"
          />

          <div className="mt-6">
            <Button.Root
              type="submit"
              size="lg"
              block
              disabled={isPending}
              isLoading={isPending}
            >
              {t('forgot_password.button.text')}
            </Button.Root>
          </div>

          <Link
            to={ROUTES.LOGIN.SIGN_IN}
            className="text-brand-400 text-sm flex gap-2 items-center justify-center mt-4"
          >
            <MoveLeft className="text-brand-600 size-4 stroke-2" />
            {t('forgot_password.back_to_login')}
          </Link>
        </form>
      </FormProvider>

      <div className="flex flex-col gap-4">
        <button
          onClick={() =>
            handleModal({
              title: t('common:app_connection.terms.title'),
              description: (
                <div className="bg-gray-50 p-4 rounded-md border max-h-[400px] overflow-y-auto">
                  <div className="text-sm text-gray-600 whitespace-pre-wrap">
                    {t('common:app_connection.terms.content')}
                  </div>
                </div>
              ),
              confirmText: t('common:app_connection.buttons.close'),
              showButtons: true,
              onConfirm: resetModal,
            })
          }
          className="block text-brand-400 text-sm text-center hover:underline"
        >
          {t('sign_in.footer.terms_of_use.text')}
        </button>
        <p className="text-base-400 font-light text-xs text-center">
          {t('sign_in.footer.copyright.text')}
        </p>
      </div>
    </div>
  )
}
